import { memo, useCallback } from "react";
import { Popup } from "@/components/popups/Popup";
import { CollectionItem } from "./CollectionItem";
import { useUserCollectionsData } from "@/common/hooks/useUserCollectionsData";
import { ICollection, IErrorData } from "@/common/services/types";
import { EventSource, metricsBus } from "@/common/buses";
import {
  onCloseAddCollectionPopup,
  onOpenCreateNewCollectionPopup
} from "@/components/popup-dispatcher/PopupDispatcher";
import { IAddToCollectionPopupProps } from "@/common/buses/popups/popup-types";
import { useUserStore } from "@/common/store/userStore";
import { apiCollections } from "@/common/services";
import { showNotification } from "@/components/notifications/Notifications";
import { AxiosError } from "axios";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { InfiniteScroll } from "@/components/utils/InfiniteScroll";

import "./AddToCollectionPopup.scss";
import { Button } from "@/components/buttons/Button";

export const AddToCollectionPopup = memo(({ gifId, position, boosted, playCount }: IAddToCollectionPopupProps) => {
  const userName = useUserStore((state) => state.currentUser?.username);
  const eventSource = useAnalyticsStore((state) => state.eventSource);
  const sourceId = eventSource?.id ?? null;
  const sourceName = eventSource?.name || EventSource.Other;

  const {
    collections,
    error,
    loadMore,
    addCollection,
    hasMore
  } = useUserCollectionsData({ userName });

  const openCreateNewCollectionPopup = () => {
    onOpenCreateNewCollectionPopup({
      onCreated: addCollection,
    });
  };

  const addToCollections = async (folderId: string) => {
    try {
      await apiCollections.addGifToCollection({
        collectionId: folderId,
        gifId: gifId,
        context: sourceId,
      });
      showNotification({
        title: "Added to Collection('s)!",
        variant: "success"
      });
      metricsBus.emit("addedToCollection", {
        gifId,
        source: sourceName,
        sourceId,
        position,
        boosted,
        playCount,
        userName,
      });
    } catch (e: unknown) {
      const err = e as AxiosError<IErrorData>;
      const message = err.response?.data?.error?.message ||
        "An error occurred, please try again later";
      showNotification({
        title: message,
        variant: "error"
      });
    }
    handleClose();
  };

  const handleClose = useCallback(() => {
    onCloseAddCollectionPopup();
  }, []);

  return (
    <Popup
      title="Add to a Collection"
      isOpen={true}
      popupClass="AddToCollectionPopup"
      onClose={handleClose}
    >
      <div id="scrollable-container">
        <InfiniteScroll
          fetchMore={loadMore}
          hasMore={hasMore}
          root="#scrollable-container"
        >
          <ul className="AddToCollectionPopup-List">
            {error ? (
              <li className="AddToCollectionPopup-NoCollection">
                We have some issues with loading collections. Please close and open the dialog and try again.
              </li>
            ) : (
              <>
                {collections?.map((collection: ICollection) => (
                  <CollectionItem
                    key={collection.folderId}
                    collection={collection}
                    addToCollections={addToCollections}
                  />
                ))}
              </>
            )}
          </ul>
        </InfiniteScroll>
      </div>
      <div className="AddToCollectionPopup-Bottom">
        <button className="cancelBtn" onClick={handleClose}>Cancel</button>
        <Button variant="primary" onClick={openCreateNewCollectionPopup} >Create a new collection</Button>
      </div>
    </Popup>
  );
});
